import { useRef, useEffect } from 'react';
import { useGetIdentity, useLogout, useCustom } from '@refinedev/core';
import { Layout, Space, Avatar, Dropdown, Typography, Input, Badge, Segmented } from 'antd';
import type { MenuProps, InputRef } from 'antd';
import { UserOutlined, LogoutOutlined, SearchOutlined, BellOutlined, FileProtectOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getLocale, setLocale, t } from '../../i18n';

const { Text } = Typography;

interface User {
  id: number;
  fullName?: string;
  username: string;
  avatar?: string;
}

interface DashboardStats {
  pendingDocuments: number;
}

// TODO: T-095 - Multi-language admin interface (UZ/RU/EN)
// Ctrl+K handled below in useEffect keyboard listener
export const Header = () => {
  const { data: user } = useGetIdentity<User>();
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();
  const searchInputRef = useRef<InputRef>(null);

  const { data: statsData } = useCustom<DashboardStats>({
    url: '/admin/dashboard',
    method: 'get',
    queryOptions: {
      refetchInterval: 60000,
    },
  });

  const pendingDocuments = statsData?.data?.pendingDocuments ?? 0;
  const totalNotifications = pendingDocuments;

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleSearch = (value: string) => {
    const trimmed = value.trim();
    if (trimmed) {
      navigate(`/users?search=${encodeURIComponent(trimmed)}`);
    }
  };

  const notificationMenuItems: MenuProps['items'] = [
    {
      key: 'documents',
      icon: <FileProtectOutlined />,
      label: `${pendingDocuments} ta kutilayotgan hujjat`,
      onClick: () => navigate('/documents'),
    },
  ];

  const userMenuItems: MenuProps['items'] = [
    {
      key: 'logout',
      label: t('common.logout'),
      icon: <LogoutOutlined />,
      danger: true,
      onClick: () => logout(),
    },
  ];

  return (
    <Layout.Header
      style={{
        background: '#fff',
        padding: '0 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #E8E8E8',
      }}
    >
      {/* Left spacer */}
      <div style={{ flex: 1 }} />

      {/* Center: Search */}
      <div style={{ flex: 'none' }}>
        <Input
          ref={searchInputRef}
          placeholder={`${t('common.search')} (Ctrl+K)`}
          prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
          style={{
            width: 300,
            borderRadius: 8,
            border: '1px solid #e8e8e8',
          }}
          onPressEnter={(e) => handleSearch((e.target as HTMLInputElement).value)}
          allowClear
        />
      </div>

      {/* Right: Language + Notifications + User */}
      <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
        <Space size={16}>
          <Segmented
            value={getLocale()}
            onChange={(value) => {
              setLocale(value as 'uz' | 'ru');
              window.location.reload();
            }}
            options={[
              { value: 'uz', label: 'UZ' },
              { value: 'ru', label: 'RU' },
            ]}
            size="small"
            style={{ fontWeight: 600 }}
          />

          <Dropdown menu={{ items: notificationMenuItems }} trigger={['click']} placement="bottomRight">
            <Badge count={totalNotifications} size="small" offset={[-2, 2]}>
              <BellOutlined
                style={{
                  fontSize: 20,
                  cursor: 'pointer',
                  color: '#595959',
                  padding: 4,
                }}
              />
            </Badge>
          </Dropdown>

          <Dropdown menu={{ items: userMenuItems }} trigger={['click']}>
            <Space style={{ cursor: 'pointer' }}>
              <Avatar
                size="small"
                icon={<UserOutlined />}
                src={user?.avatar}
                style={{ backgroundColor: '#1ABC9C' }}
              />
              <Text strong>{user?.fullName || user?.username || 'Admin'}</Text>
            </Space>
          </Dropdown>
        </Space>
      </div>
    </Layout.Header>
  );
};
